import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import SortRoundedIcon from "@mui/icons-material/SortRounded";

const MAX_TIERS = 20;
const MAX_QUANTITY = 999;

function number(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function quantityDiscountEligibility(form = {}) {
  if (form.erpItemKind !== "product") {
    return { allowed: false, reason: "Disponible únicamente para productos vendibles." };
  }
  if (form.sellable === false) {
    return { allowed: false, reason: "Habilitá la venta del producto para usar estos tramos." };
  }
  if (form.priceType !== "fixed") {
    return { allowed: false, reason: "Requiere precio fijo; no se aplica a precios desde o a consultar." };
  }
  if (!(number(form.price) > 0)) {
    return { allowed: false, reason: "Definí primero un precio principal mayor que cero." };
  }
  return { allowed: true, reason: "" };
}

export function validateQuantityDiscountRows(form = {}) {
  const rows = Array.isArray(form.quantityPriceTiers) ? form.quantityPriceTiers : [];
  if (!rows.length) return "";
  const eligibility = quantityDiscountEligibility(form);
  // El payload elimina los tramos al pasar el ítem a una configuración no
  // compatible. Así se puede convertir un producto en servicio o consulta.
  if (!eligibility.allowed) return "";

  const sorted = rows
    .map((row) => ({ minQuantity: Number(row.minQuantity), unitPrice: Number(row.unitPrice) }))
    .sort((a, b) => a.minQuantity - b.minQuantity);
  const seen = new Set();
  let previousPrice = Number(form.price);

  for (const row of sorted) {
    if (!Number.isInteger(row.minQuantity) || row.minQuantity < 2 || row.minQuantity > MAX_QUANTITY) {
      return "Cada tramo debe comenzar desde una cantidad entera entre 2 y 999.";
    }
    if (seen.has(row.minQuantity)) return `La cantidad ${row.minQuantity} está repetida.`;
    seen.add(row.minQuantity);
    if (!Number.isFinite(row.unitPrice) || row.unitPrice <= 0) {
      return "Cada tramo necesita un precio unitario mayor que cero.";
    }
    if (row.unitPrice >= previousPrice) {
      return previousPrice === Number(form.price)
        ? "El primer precio por cantidad debe ser menor que el precio principal."
        : "A mayor cantidad, el precio unitario debe disminuir.";
    }
    previousPrice = row.unitPrice;
  }
  return "";
}

export function normalizeQuantityDiscountRows(rows = []) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      minQuantity: Number(row.minQuantity),
      unitPrice: Number(row.unitPrice),
    }))
    .sort((a, b) => a.minQuantity - b.minQuantity);
}

export default function QuantityDiscountsEditor({ form, setForm, compact = false }) {
  const rows = Array.isArray(form.quantityPriceTiers) ? form.quantityPriceTiers : [];
  const eligibility = quantityDiscountEligibility(form);
  const validationMessage = validateQuantityDiscountRows(form);
  const basePrice = number(form.price);

  const updateRow = (index, key, value) => {
    setForm((current) => ({
      ...current,
      quantityPriceTiers: (current.quantityPriceTiers || []).map((row, rowIndex) =>
        rowIndex === index ? { ...row, [key]: value } : row,
      ),
    }));
  };

  const addRow = () => {
    setForm((current) => {
      const currentRows = Array.isArray(current.quantityPriceTiers)
        ? current.quantityPriceTiers
        : [];
      if (currentRows.length >= MAX_TIERS) return current;
      const orderedRows = [...currentRows].sort(
        (a, b) => Number(a.minQuantity || 0) - Number(b.minQuantity || 0),
      );
      const last = orderedRows[orderedRows.length - 1];
      const suggestedQuantity = Math.max(2, Number(last?.minQuantity || 1) + 1);
      const suggestedPrice = last?.unitPrice || current.price || "";
      return {
        ...current,
        quantityPriceTiers: [
          ...currentRows,
          {
            key: `quantity-tier-${Date.now()}-${currentRows.length}`,
            minQuantity: String(suggestedQuantity),
            unitPrice: String(suggestedPrice),
          },
        ],
      };
    });
  };

  const removeRow = (index) => {
    setForm((current) => ({
      ...current,
      quantityPriceTiers: (current.quantityPriceTiers || []).filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const sortRows = () => {
    setForm((current) => ({
      ...current,
      quantityPriceTiers: [...(current.quantityPriceTiers || [])].sort(
        (left, right) => Number(left.minQuantity || 0) - Number(right.minQuantity || 0),
      ),
    }));
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3 }}>
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          spacing={1}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography sx={{ fontWeight: 900, color: "#19242F" }}>
                Descuentos por cantidad
              </Typography>
              {rows.length ? <Chip size="small" label={`${rows.length} tramo${rows.length === 1 ? "" : "s"}`} /> : null}
            </Stack>
            {!compact ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                Definí el precio unitario del producto principal desde cada cantidad.
              </Typography>
            ) : null}
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              type="button"
              size="small"
              startIcon={<SortRoundedIcon />}
              onClick={sortRows}
              disabled={rows.length < 2}
              sx={{ textTransform: "none", fontWeight: 800 }}
            >
              Ordenar
            </Button>
            <Button
              type="button"
              size="small"
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              onClick={addRow}
              disabled={!eligibility.allowed || rows.length >= MAX_TIERS}
              sx={{ textTransform: "none", fontWeight: 850 }}
            >
              Agregar tramo
            </Button>
          </Stack>
        </Stack>

        {!eligibility.allowed ? (
          <Alert severity="info">
            {eligibility.reason} Los tramos existentes se quitarán al guardar esta configuración.
          </Alert>
        ) : null}
        {eligibility.allowed && validationMessage ? <Alert severity="warning">{validationMessage}</Alert> : null}

        {rows.length ? <Divider /> : null}
        <Stack spacing={1.1}>
          {rows.map((row, index) => {
            const tierPrice = number(row.unitPrice);
            const discount = basePrice > 0 && tierPrice > 0
              ? Math.max(0, (1 - tierPrice / basePrice) * 100)
              : 0;
            return (
              <Stack
                key={row.key || `quantity-tier-${index}`}
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ sm: "center" }}
              >
                <TextField
                  label="Desde"
                  type="number"
                  value={row.minQuantity}
                  onChange={(event) => updateRow(index, "minQuantity", event.target.value)}
                  disabled={!eligibility.allowed}
                  inputProps={{ min: 2, max: MAX_QUANTITY, step: 1 }}
                  helperText={form.unit ? `${form.unit}${Number(row.minQuantity) === 1 ? "" : "s"}` : "unidades"}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Precio unitario"
                  type="number"
                  value={row.unitPrice}
                  onChange={(event) => updateRow(index, "unitPrice", event.target.value)}
                  disabled={!eligibility.allowed}
                  inputProps={{ min: 0.01, step: "0.01" }}
                  helperText={discount > 0 ? `${discount.toLocaleString("es-AR", { maximumFractionDigits: 2 })}% menos` : "Debe bajar respecto del tramo anterior"}
                  sx={{ flex: 1 }}
                />
                <Tooltip title="Eliminar tramo">
                  <span>
                    <IconButton
                      type="button"
                      color="error"
                      onClick={() => removeRow(index)}
                      aria-label={`Eliminar tramo ${index + 1}`}
                    >
                      <DeleteRoundedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            );
          })}
        </Stack>

        {compact ? (
          <Box
            component="details"
            sx={{
              borderRadius: 2,
              px: 1.2,
              py: 0.8,
              bgcolor: "rgba(46,125,50,.06)",
              color: "rgba(25,36,47,.72)",
              "& summary": { cursor: "pointer", fontWeight: 850, fontSize: 13 },
            }}
          >
            <Typography component="summary">Cómo se calcula</Typography>
            <Typography variant="caption" sx={{ display: "block", mt: 0.8, lineHeight: 1.5 }}>
              El tramo parte del precio principal. Las variantes mantienen su diferencia proporcional y luego se aplica la lista de precios del cliente.
            </Typography>
          </Box>
        ) : (
          <Alert severity="success" icon={false} sx={{ py: 0.8 }}>
            Regla única: el tramo se calcula sobre el precio principal; las variantes conservan proporcionalmente su diferencia de precio y, después, se aplica la lista de precios personal del cliente con su redondeo.
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}
